// Copyright 2025-2026 Florian Gallon. All Rights reserved.
// One-time ingredient migration utility

import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { ensureSeededIngredients, createCustomIngredient } from './ingredientsUtils';

// In-memory lock to prevent concurrent migrations
const migrationLocks = new Map();

/**
 * Remove duplicate ingredients, keeping the oldest one (first created)
 */
async function removeDuplicateIngredients(bakeryId) {
  const snap = await getDocs(query(
    collection(db, 'ingredients'),
    where('bakeryId', '==', bakeryId)
  ));

  // Group by key
  const byKey = new Map();
  snap.docs.forEach(d => {
    const data = d.data();
    const key = data.key;
    if (!key) return;

    if (!byKey.has(key)) {
      byKey.set(key, []);
    }
    byKey.get(key).push({ id: d.id, ref: d.ref, data, createdAt: data.createdAt || '' });
  });

  let removed = 0;
  for (const [key, docs] of byKey) {
    if (docs.length > 1) {
      // Sort by createdAt, keep oldest
      docs.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      // Delete all but the first (oldest)
      for (let i = 1; i < docs.length; i++) {
        await deleteDoc(docs[i].ref);
        removed++;
      }
      console.log(`[Migration] Removed ${docs.length - 1} duplicates for key: ${key}`);
    }
  }
  return removed;
}

/**
 * One-time migration: customIngredients → ingredients collection
 * Also updates stock purchases, recipes, and products to use ingredient keys
 *
 * @param {string} bakeryId - The bakery ID to migrate
 * @returns {Promise<{status: string, customMigrated?: number, stockMigrated?: number, recipesMigrated?: number, productsMigrated?: number}>}
 */
export async function migrateIngredients(bakeryId) {
  // Check in-memory lock first (prevents React double-trigger)
  if (migrationLocks.get(bakeryId)) {
    console.log('[Migration] Already running for bakery:', bakeryId);
    return { status: 'already_running' };
  }
  migrationLocks.set(bakeryId, true);

  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    const bakerySnap = await getDoc(bakeryRef);
    if (bakerySnap.data()?.ingredientsMigrationComplete) {
      return { status: 'already_done' };
    }

    console.log('[Migration] Starting ingredient migration for bakery:', bakeryId);

    // 1. Ensure system ingredients exist
    await ensureSeededIngredients(bakeryId, { force: true });

    // 2. Clean up any existing duplicates first
    const duplicatesRemoved = await removeDuplicateIngredients(bakeryId);
    console.log(`[Migration] Removed ${duplicatesRemoved} duplicate ingredients`);

    // 3. Build name → key lookup from existing ingredients (after dedup)
    const nameToKey = new Map();
    const ingredientsSnap = await getDocs(query(
      collection(db, 'ingredients'),
      where('bakeryId', '==', bakeryId)
    ));
    ingredientsSnap.docs.forEach(d => {
      const data = d.data();
      const nameEn = (data.name?.en || '').toLowerCase().trim();
      const nameFr = (data.name?.fr || '').toLowerCase().trim();
      if (nameEn) nameToKey.set(nameEn, data.key);
      if (nameFr && nameFr !== nameEn) nameToKey.set(nameFr, data.key);
      // Also map key to itself (so existing keys are preserved)
      if (data.key) nameToKey.set(data.key.toLowerCase(), data.key);
    });

    // 4. Migrate customIngredients collection
    const customSnap = await getDocs(query(
      collection(db, 'customIngredients'),
      where('bakeryId', '==', bakeryId)
    ));

    let customMigrated = 0;
    for (const customDoc of customSnap.docs) {
      const name = customDoc.data().name?.trim();
      if (!name) continue;

      const normalized = name.toLowerCase().trim();
      if (!nameToKey.has(normalized)) {
        const newKey = await createCustomIngredient(bakeryId, name, 'en');
        nameToKey.set(normalized, newKey);
        customMigrated++;
      }
    }
    console.log(`[Migration] Migrated ${customMigrated} custom ingredients`);

    // 5. Migrate stock purchases
    const stockMigrated = await migrateStockPurchases(bakeryId, nameToKey);
    console.log(`[Migration] Updated ${stockMigrated} stock purchases`);

    // 6. Migrate recipes
    const recipesMigrated = await migrateRecipes(bakeryId, nameToKey);
    console.log(`[Migration] Updated ${recipesMigrated} recipes`);

    // 7. Migrate product variation ingredients
    const productsMigrated = await migrateProducts(bakeryId, nameToKey);
    console.log(`[Migration] Updated ${productsMigrated} products`);

    // 8. Mark complete
    await updateDoc(bakeryRef, {
      ingredientsMigrationComplete: true,
      ingredientsMigrationDate: new Date().toISOString()
    });

    console.log('[Migration] Complete:', { duplicatesRemoved, customMigrated, stockMigrated, recipesMigrated, productsMigrated });
    return { status: 'done', duplicatesRemoved, customMigrated, stockMigrated, recipesMigrated, productsMigrated };
  } finally {
    // Always release the lock
    migrationLocks.delete(bakeryId);
  }
}

async function migrateStockPurchases(bakeryId, nameToKey) {
  const snap = await getDocs(query(
    collection(db, 'stockPurchases'),
    where('bakeryId', '==', bakeryId)
  ));

  let updated = 0;
  for (const purchaseDoc of snap.docs) {
    const purchase = purchaseDoc.data();
    const currentRef = purchase.ingredientKey || purchase.ingredientName;
    if (!currentRef) continue;

    // Check if current ref is already a valid key
    const normalized = currentRef.toLowerCase().trim();
    const correctKey = nameToKey.get(normalized);

    if (correctKey && correctKey !== currentRef) {
      await updateDoc(purchaseDoc.ref, {
        ingredientKey: correctKey,
        ingredientName: correctKey
      });
      updated++;
    }
  }
  return updated;
}

async function migrateRecipes(bakeryId, nameToKey) {
  const snap = await getDocs(query(
    collection(db, 'recipes'),
    where('bakeryId', '==', bakeryId)
  ));

  let updated = 0;
  for (const recipeDoc of snap.docs) {
    const recipe = recipeDoc.data();
    let needsUpdate = false;

    // Helper to convert ingredient names to keys
    const convertIngredient = (ing) => {
      const name = typeof ing === 'string' ? ing : ing?.name;
      if (!name) return ing;

      const normalized = name.toLowerCase().trim();
      const correctKey = nameToKey.get(normalized);

      if (correctKey && correctKey !== name) {
        needsUpdate = true;
        return typeof ing === 'string'
          ? correctKey
          : { ...ing, name: correctKey };
      }
      return ing;
    };

    // Update sections if present
    let updatedSections = null;
    if (recipe.sections) {
      updatedSections = recipe.sections.map(section => ({
        ...section,
        ingredients: (section.ingredients || []).map(convertIngredient)
      }));
    }

    // Update legacy ingredients field if present (used by stockUtils, ProductionPlanning)
    let updatedIngredients = null;
    if (recipe.ingredients) {
      updatedIngredients = recipe.ingredients.map(convertIngredient);
    }

    if (needsUpdate) {
      const updateData = {};
      if (updatedSections) updateData.sections = updatedSections;
      if (updatedIngredients) updateData.ingredients = updatedIngredients;

      await updateDoc(recipeDoc.ref, updateData);
      updated++;
    }
  }
  return updated;
}

async function migrateProducts(bakeryId, nameToKey) {
  const snap = await getDocs(query(
    collection(db, 'products'),
    where('bakeryId', '==', bakeryId)
  ));

  let updated = 0;
  for (const productDoc of snap.docs) {
    const product = productDoc.data();
    if (!product.variationIngredients?.length) continue;

    let needsUpdate = false;
    const updatedVariations = product.variationIngredients.map(v => {
      const name = v.name;
      if (!name) return v;

      const normalized = name.toLowerCase().trim();
      const correctKey = nameToKey.get(normalized);

      if (correctKey && correctKey !== name) {
        needsUpdate = true;
        return { ...v, name: correctKey };
      }
      return v;
    });

    if (needsUpdate) {
      await updateDoc(productDoc.ref, { variationIngredients: updatedVariations });
      updated++;
    }
  }
  return updated;
}
